import { Component } from "react";
import { logError } from "../lib/errorLogger";

/**
 * Error boundary that catches chunk load errors (from lazy imports after deploys).
 * Instead of showing a white screen, it auto-reloads once or shows a friendly message.
 */
export class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    const isChunk =
      error?.name === "ChunkLoadError" ||
      error?.message?.includes("Loading chunk") ||
      error?.message?.includes("Loading CSS chunk") ||
      error?.message?.includes("dynamically imported module");
    return { hasError: true, isChunkError: isChunk };
  }

  componentDidCatch(error, errorInfo) {
    if (this.state.isChunkError) {
      // Check if we already tried reloading
      const reloadKey = "chunk_error_reload";
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      const reloading = !lastReload || now - parseInt(lastReload) > 30000;
      // iter 429 — every retry/reload page is logged (who, when, which chunk)
      logError({ action: reloading ? "stale_bundle_reload" : "reload_page_shown", source: "chunk", error,
                 extra: { where: "ChunkErrorBoundary", componentStack: errorInfo?.componentStack?.slice(0, 600) } });
      if (reloading) {
        sessionStorage.setItem(reloadKey, now.toString());
        window.location.reload();
        return;
      }
    } else {
      logError({ action: "react_render_crash", source: "boundary", error,
                 extra: { where: "ChunkErrorBoundary", componentStack: errorInfo?.componentStack?.slice(0, 600) } });
    }
    console.error("Error boundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            height: "100vh", background: "#0C0C0E", color: "#a1a1aa", gap: "16px"
          }}>
            <p style={{ fontSize: "14px" }}>A new version was deployed. Refreshing...</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "8px 24px", background: "#007AFF", color: "white",
                border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px"
              }}
            >
              Reload Now
            </button>
          </div>
        );
      }
      return this.props.fallback || (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          height: "100vh", background: "#0C0C0E", color: "#a1a1aa", gap: "16px"
        }}>
          <p style={{ fontSize: "14px" }}>Something went wrong.</p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{
              padding: "8px 24px", background: "#333", color: "white",
              border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px"
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
